import { create } from "zustand";
import { BOX_TIERS } from "@/lib/appstle/config";
import type { Product } from "@/lib/shopify/types";

type BoxItem = {
	product: Product;
	quantity: number;
};

export type BoxTagFilter = Record<string, "neutral" | "include">;

interface BuildBoxState {
	selectedItems: Map<string, BoxItem>;
	targetSize: number;
	purchaseType: "onetime" | "subscribe";

	// Client-side product cache for fallback when server fetch fails
	cachedProducts: Product[];

	// Filter state
	searchQuery: string;
	categoryFilter: string;
	sortBy: string;
	dietTagFilter: BoxTagFilter;
	meatTagFilter: BoxTagFilter;
	typeTagFilter: BoxTagFilter;

	setTargetSize: (size: number) => void;
	setPurchaseType: (type: "onetime" | "subscribe") => void;
	addToBox: (product: Product) => void;
	removeFromBox: (product: Product, removeAll?: boolean) => void;
	setItemQuantity: (product: Product, quantity: number) => void;
	resetBox: () => void;
	trimToSize: (newSize: number) => void;
	getTotalMeals: () => number;

	// Filter actions
	setSearchQuery: (q: string) => void;
	setCategoryFilter: (v: string) => void;
	setSortBy: (v: string) => void;
	setDietTagFilter: (v: BoxTagFilter) => void;
	setMeatTagFilter: (v: BoxTagFilter) => void;
	setTypeTagFilter: (v: BoxTagFilter) => void;
	setCachedProducts: (products: Product[]) => void;
	resetFilters: () => void;
}

const defaultFilters = {
	searchQuery: "",
	categoryFilter: "all",
	sortBy: "best-selling",
	dietTagFilter: {} as BoxTagFilter,
	meatTagFilter: {} as BoxTagFilter,
	typeTagFilter: {} as BoxTagFilter,
};

const ALLOWED_BOX_SIZES = new Set(BOX_TIERS.map((tier) => tier.count));

function normalizePositiveStockValue(value: unknown): number | null {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0)
		return null;
	return Math.trunc(value);
}

export function getProductStockLimit(product: Product): number | null {
	if (!product.availableForSale) return 0;

	const primaryVariant = product.variants[0];
	if (primaryVariant && primaryVariant.availableForSale === false) return 0;

	const totalInventory = normalizePositiveStockValue(product.totalInventory);
	const variantInventory = normalizePositiveStockValue(
		primaryVariant?.quantityAvailable,
	);

	if (totalInventory === null && variantInventory === null) return null;
	if (totalInventory === null) return variantInventory;
	if (variantInventory === null) return totalInventory;

	return Math.min(totalInventory, variantInventory);
}

export const useBuildBoxStore = create<BuildBoxState>((set, get) => ({
	selectedItems: new Map(),
	targetSize: 40,
	purchaseType: "subscribe",
	cachedProducts: [],

	// Filter initial state
	...defaultFilters,

	setTargetSize: (size) => {
		if (!ALLOWED_BOX_SIZES.has(size)) return;

		const { targetSize, trimToSize } = get();
		if (size !== targetSize) {
			set({ targetSize: size });
			trimToSize(size);
		}
	},

	setPurchaseType: (type) => set({ purchaseType: type }),

	addToBox: (product) =>
		set((state) => {
			const currentTotal = Array.from(state.selectedItems.values()).reduce(
				(sum, item) => sum + item.quantity,
				0,
			);

			if (currentTotal >= state.targetSize) {
				return state;
			}

			const stockLimit = getProductStockLimit(product);
			if (stockLimit !== null && stockLimit <= 0) {
				return state;
			}

			const newMap = new Map(state.selectedItems);
			const existing = newMap.get(product.id);

			if (existing) {
				const nextQuantity = existing.quantity + 1;
				if (stockLimit !== null && nextQuantity > stockLimit) {
					return state;
				}

				newMap.set(product.id, {
					...existing,
					quantity: nextQuantity,
				});
			} else {
				newMap.set(product.id, { product, quantity: 1 });
			}

			return { selectedItems: newMap };
		}),

	removeFromBox: (product, removeAll = false) =>
		set((state) => {
			const newMap = new Map(state.selectedItems);

			if (removeAll) {
				newMap.delete(product.id);
			} else {
				const existing = newMap.get(product.id);
				if (existing && existing.quantity > 1) {
					newMap.set(product.id, {
						...existing,
						quantity: existing.quantity - 1,
					});
				} else {
					newMap.delete(product.id);
				}
			}

			return { selectedItems: newMap };
		}),

	setItemQuantity: (product, quantity) =>
		set((state) => {
			const newMap = new Map(state.selectedItems);
			const normalizedQuantity = Number.isFinite(quantity)
				? Math.max(0, Math.trunc(quantity))
				: 0;

			if (normalizedQuantity <= 0) {
				newMap.delete(product.id);
				return { selectedItems: newMap };
			}

			const currentTotal = Array.from(state.selectedItems.values()).reduce(
				(sum, item) =>
					sum + (item.product.id === product.id ? 0 : item.quantity),
				0,
			);

			const maxAllowedBySize = state.targetSize - currentTotal;
			const stockLimit = getProductStockLimit(product);
			const maxAllowed =
				stockLimit === null
					? maxAllowedBySize
					: Math.min(maxAllowedBySize, stockLimit);
			const clampedQuantity = Math.min(normalizedQuantity, maxAllowed);

			if (clampedQuantity <= 0) {
				newMap.delete(product.id);
			} else {
				newMap.set(product.id, { product, quantity: clampedQuantity });
			}

			return { selectedItems: newMap };
		}),

	resetBox: () =>
		set((state) => ({
			selectedItems: new Map(),
			targetSize: state.targetSize,
			...defaultFilters,
		})),

	trimToSize: (newSize) =>
		set((state) => {
			const currentTotal = Array.from(state.selectedItems.values()).reduce(
				(sum, item) => sum + item.quantity,
				0,
			);

			if (currentTotal <= newSize) return state;

			const itemsToTrim = currentTotal - newSize;
			let trimmedCount = 0;
			const newMap = new Map(state.selectedItems);
			const entries = Array.from(newMap.entries());

			for (const [id, item] of entries) {
				if (trimmedCount >= itemsToTrim) break;

				const removeAmount = Math.min(
					item.quantity,
					itemsToTrim - trimmedCount,
				);
				if (item.quantity - removeAmount <= 0) {
					newMap.delete(id);
				} else {
					newMap.set(id, { ...item, quantity: item.quantity - removeAmount });
				}
				trimmedCount += removeAmount;
			}

			return { selectedItems: newMap };
		}),

	getTotalMeals: () => {
		const { selectedItems } = get();
		return Array.from(selectedItems.values()).reduce(
			(sum, item) => sum + item.quantity,
			0,
		);
	},

	// Filter actions
	setSearchQuery: (q) => set({ searchQuery: q }),
	setCategoryFilter: (v) => set({ categoryFilter: v }),
	setSortBy: (v) => set({ sortBy: v }),
	setDietTagFilter: (v) => set({ dietTagFilter: v }),
	setMeatTagFilter: (v) => set({ meatTagFilter: v }),
	setTypeTagFilter: (v) => set({ typeTagFilter: v }),
	setCachedProducts: (products) =>
		set((state) => ({
			cachedProducts: products.length > 0 ? products : state.cachedProducts,
		})),
	resetFilters: () => set(defaultFilters),
}));
